import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Github } from 'lucide-react';
import projectsData from '../data/projects';
import { stampContainer, stampUp, viewportOnce } from '../lib/motion';

// Fixed bento spans for the known, fixed-length project list: a real
// masonry algorithm would be overkill for seven hand-picked cards.
const spans = [
  'sm:col-span-2 lg:col-span-4',
  'lg:col-span-2',
  'lg:col-span-2',
  'lg:col-span-2',
  'lg:col-span-2',
  'lg:col-span-3',
  'lg:col-span-3',
];

const tileColors = ['bg-sun', 'bg-bubble', 'bg-sky', 'bg-mint'];

const getInitials = (title) =>
  title
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const Projects = () => {
  return (
    <section id="projects" className="py-20 md:py-28 bg-paper dark:bg-noir">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.4 }}
          className="text-center mb-14"
        >
          <p className="font-display font-bold text-ink/60 dark:text-cream/60 mb-2">Things I've shipped</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-ink dark:text-cream">
            Selected{' '}
            <span className="inline-block -rotate-1 bg-sky px-3 brutal-border brutal-shadow-sm">
              Work
            </span>
          </h2>
        </motion.div>

        <motion.div
          variants={stampContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6"
        >
          {projectsData.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} className={spans[index] || 'lg:col-span-2'} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="text-center mt-14"
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ink dark:bg-cream text-cream dark:text-ink font-display font-bold brutal-border brutal-hover"
          >
            Got a project in mind?
            <ArrowUpRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project, index, className }) => {
  const [imageError, setImageError] = useState(false);
  const tile = tileColors[index % tileColors.length];

  return (
    <motion.div
      variants={stampUp}
      className={`group rounded-2xl overflow-hidden bg-cream dark:bg-card brutal-border brutal-shadow brutal-hover flex flex-col ${className}`}
    >
      <div className={`relative h-48 overflow-hidden border-b-[3px] border-ink dark:border-cream ${!imageError && project.image ? 'bg-[#f5f0e6]' : tile}`}>
        {!imageError && project.image ? (
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center brutal-grid-bg">
            <span className="font-display text-5xl font-extrabold text-ink/50">
              {getInitials(project.title)}
            </span>
          </div>
        )}
        {project.featured && (
          <span className="absolute top-3 left-3 px-3 py-1 text-xs font-extrabold rounded-full bg-sun text-ink brutal-border-thin rotate-2">
            Featured
          </span>
        )}
        {project.personal && (
          <span className="absolute top-3 right-3 px-3 py-1 text-xs font-extrabold rounded-full bg-sky text-ink brutal-border-thin -rotate-2">
            Personal Project
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-lg font-extrabold text-ink dark:text-cream mb-2">
          {project.title}
        </h3>
        <p className="text-sm text-ink/70 dark:text-cream/70 leading-relaxed mb-4 flex-1">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          {project.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 text-xs font-bold rounded-full text-ink dark:text-cream brutal-border-thin"
            >
              {tag}
            </span>
          ))}
        </div>

        {(project.liveLink || project.codeLink) && (
          <div className="flex gap-3 mt-auto">
            {project.liveLink && (
              <a
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-ink dark:bg-cream text-cream dark:text-ink text-sm font-bold brutal-border-thin brutal-hover"
              >
                View
                <ArrowUpRight className="w-4 h-4" />
              </a>
            )}
            {project.codeLink && (
              <a
                href={project.codeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-paper dark:bg-noir text-ink dark:text-cream text-sm font-bold brutal-border-thin brutal-hover"
                title="View source code"
              >
                <Github className="w-4 h-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Projects;
